import React, { useState, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell } from 'recharts';
import { Calendar, BarChart3, PieChart as PieChartIcon } from 'lucide-react';

const PerformanceCharts = ({ trackingLinks, clickData, dateRange }) => {
  const [activeTab, setActiveTab] = useState('trends');

  // Process data for trend chart
  const trendData = useMemo(() => {
    if (!clickData || clickData?.length === 0) return [];

    const last30Days = Array.from({ length: parseInt(dateRange) }, (_, i) => {
      const date = new Date();
      date?.setDate(date?.getDate() - (parseInt(dateRange) - 1 - i));
      return date?.toISOString()?.split('T')?.[0];
    });

    const dailyClicks = last30Days?.reduce((acc, date) => {
      acc[date] = {
        date,
        total_clicks: 0,
        unique_clicks: 0,
        formattedDate: new Date(date)?.toLocaleDateString('fr-FR', { 
          day: 'numeric', 
          month: 'short' 
        })
      };
      return acc;
    }, {});

    clickData?.forEach(click => {
      const clickDate = new Date(click.clicked_at)?.toISOString()?.split('T')?.[0];
      if (dailyClicks?.[clickDate]) {
        dailyClicks[clickDate].total_clicks += 1;
        // For unique clicks, you'd need to implement session-based logic // For now, we'll use a simplified approach
        dailyClicks[clickDate].unique_clicks += 1;
      }
    });

    return Object.values(dailyClicks);
  }, [clickData, dateRange]);

  // Process data for source distribution
  const sourceData = useMemo(() => {
    if (!clickData || clickData?.length === 0) return [];

    const sources = clickData?.reduce((acc, click) => {
      const source = click?.source || 'unknown';
      acc[source] = (acc?.[source] || 0) + 1;
      return acc;
    }, {});

    const colors = {
      'qr_code': '#6366f1',
      'direct_link': '#10b981',
      'social_media': '#f59e0b',
      'email': '#ef4444',
      'sms': '#8b5cf6',
      'other': '#6b7280'
    };

    return Object.entries(sources)?.map(([source, count]) => ({
      source: source?.replace('_', ' ')?.toUpperCase(),
      count,
      color: colors?.[source] || '#6b7280'
    }));
  }, [clickData]);

  // Process data for device breakdown
  const deviceData = useMemo(() => {
    if (!clickData || clickData?.length === 0) return [];

    const devices = clickData?.reduce((acc, click) => {
      const device = click?.device_type || 'unknown';
      acc[device] = (acc?.[device] || 0) + 1;
      return acc;
    }, {});

    return Object.entries(devices)?.map(([device, count]) => ({
      device: device?.charAt(0)?.toUpperCase() + device?.slice(1),
      count
    }));
  }, [clickData]);

  const tabs = [
    { id: 'trends', name: 'Tendances', icon: BarChart3 },
    { id: 'sources', name: 'Sources', icon: PieChartIcon },
    { id: 'devices', name: 'Appareils', icon: Calendar }
  ];

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex" aria-label="Tabs">
          {tabs?.map((tab) => {
            const IconComponent = tab?.icon;
            return (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`
                  w-1/3 py-2 px-1 text-center border-b-2 font-medium text-sm flex items-center justify-center space-x-2
                  ${activeTab === tab?.id
                    ? 'border-indigo-500 text-indigo-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                <IconComponent className="h-4 w-4" />
                <span>{tab?.name}</span>
              </button>
            );
          })}
        </nav>
      </div>
      <div className="p-6">
        {/* Trends Chart */}
        {activeTab === 'trends' && (
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Évolution des Clics ({dateRange} derniers jours)
            </h3>
            <div style={{ width: '100%', height: '400px' }}>
              <ResponsiveContainer>
                <LineChart data={trendData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="formattedDate" 
                    tick={{ fontSize: 12 }}
                    interval="preserveStartEnd"
                  />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip 
                    formatter={(value, name) => [
                      value, 
                      name === 'total_clicks' ? 'Clics totaux' : 'Clics uniques'
                    ]}
                    labelFormatter={(label) => `Date: ${label}`}
                  />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="total_clicks" 
                    stroke="#6366f1" 
                    strokeWidth={2}
                    name="Clics totaux"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="unique_clicks" 
                    stroke="#10b981" 
                    strokeWidth={2}
                    name="Clics uniques"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Sources Chart */}
        {activeTab === 'sources' && (
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Répartition par Source
            </h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div style={{ width: '100%', height: '300px' }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={sourceData}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      dataKey="count"
                      label={({ source, percent }) => `${source} (${(percent * 100)?.toFixed(0)}%)`}
                    >
                      {sourceData?.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry?.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              
              <div className="space-y-3">
                {sourceData?.map((source, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div 
                        className="w-4 h-4 rounded-full" 
                        style={{ backgroundColor: source?.color }}
                      />
                      <span className="text-sm font-medium text-gray-900">
                        {source?.source}
                      </span>
                    </div>
                    <span className="text-sm font-bold text-gray-700">
                      {source?.count} clics
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Devices Chart */}
        {activeTab === 'devices' && (
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Répartition par Appareil
            </h3>
            <div style={{ width: '100%', height: '300px' }}>
              <ResponsiveContainer>
                <BarChart data={deviceData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="device" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip 
                    formatter={(value) => [value, 'Clics']}
                    labelFormatter={(label) => `Appareil: ${label}`}
                  />
                  <Bar dataKey="count" fill="#6366f1" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Empty State */}
        {(!clickData || clickData?.length === 0) && (
          <div className="text-center py-8">
            <BarChart3 className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">
              Aucune donnée disponible
            </h3>
            <p className="mt-2 text-sm text-gray-500">
              Les graphiques apparaîtront une fois que vos liens auront généré du trafic
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PerformanceCharts;